import { useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useWebsitePages } from "@api/websites.hooks";
import { usePreviewRoute, useSetPreviewRoute } from "@stores/previewStore";
import { cn } from "~/lib/utils";
import { PagePicker } from "./PagePicker";

/**
 * The preview's page switcher: binds the presentational {@link PagePicker} to
 * the preview store, so selecting a page drives the iframe route. This is the
 * side-effect half — everything here is store wiring; the dropdown UI lives in
 * PagePicker so other surfaces can reuse it without the preview coupling.
 *
 * Lives above the preview iframe so it's always reachable — a site with 50
 * pages scales via the search box rather than a scrolling tab strip.
 */
export function PageSwitcher({ websiteId }: { websiteId: number }) {
  const { data: pages = [] } = useWebsitePages(websiteId);
  const activeRoute = usePreviewRoute();
  const setActiveRoute = useSetPreviewRoute();

  // Seed the picker once pages arrive so the iframe URL and the trigger label
  // stay in lockstep without requiring a click first. Prefer the index route
  // `/` — pages come back ordered by `created_at`, so `pages[0]` is whatever
  // file committed first, which isn't reliably the home page on a multi-page
  // template generation.
  useEffect(() => {
    if (activeRoute !== null || pages.length === 0) return;
    const seed = pages.find((p) => p.route === "/")?.route ?? pages[0]?.route;
    if (seed) setActiveRoute(seed);
  }, [activeRoute, pages, setActiveRoute]);

  // Empty state — the toolbar keeps the Page control present (disabled) before
  // the build generates any pages, rather than vanishing.
  if (pages.length === 0) {
    return (
      <button
        type="button"
        disabled
        data-testid="page-picker-trigger"
        className={cn(
          "flex w-64 shrink-0 items-center justify-between gap-2",
          "rounded-lg border border-neutral-300 bg-white px-3 py-2.5",
          "text-sm text-left text-base-400 cursor-not-allowed"
        )}
      >
        <span className="truncate">No pages yet</span>
        <ChevronDownIcon className="size-4 text-base-400 shrink-0" />
      </button>
    );
  }

  return <PagePicker pages={pages} value={activeRoute} onChange={setActiveRoute} />;
}
